// Statement- and expression-shape classifiers — the sub-positions where the
// next token is structural (a CASE branch keyword, `SET`, `VALUES`, the
// statement a CTE prolog belongs to) rather than a name from the schema.
//
// Same CONFIDENT-ONLY contract as `nextKeywordSlot.ts`, which owns the clause
// tails and delegates here: every function returns `null` unless the text scan
// is clearly at the named boundary, and the resolver then falls back to its
// full emission. PURE text scan — no parse, no async, safe on the hot path.

import type { CompletionPositionKind } from '@/core/engines';

import type { NextKeywordSlot } from './legalNextKeywords';
import { scanRegion, slotScanEnd, wordBefore, type ScanOptions } from './slotTextScan';

/** Tail chars that close a completed operand (identifier, literal, call, or a
 *  parenthesized group). Anything else means an operand is still expected. */
const OPERAND_TAIL = /[A-Za-z0-9_)\]"'`]/;

/** Words that end a token sequence without ending an operand — a trailing
 *  keyword means the expression is still being written. */
const OPEN_EXPRESSION_KEYWORDS: ReadonlySet<string> = new Set([
  'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN', 'IS', 'CASE', 'WHEN', 'THEN',
  'ELSE', 'BY', 'SELECT', 'FROM', 'WHERE', 'VALUES', 'SET', 'AS', 'DISTINCT',
  'ALL', 'INTO', 'ON', 'UPDATE', 'INSERT', 'DELETE', 'WITH', 'OVER',
  'PARTITION', 'ORDER', 'GROUP', 'HAVING',
]);

/**
 * True when the token before the cursor completes an operand — an identifier,
 * a literal, a closing paren or a delimited identifier — and is not a keyword
 * that leaves the expression open. This is the "an expression just ended here"
 * test the CASE / CTE / INSERT shapes below all need.
 */
export function isAfterCompletedOperand(
  text: string,
  scanEnd: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  if (scan.lastCodeEnd <= statementStart) return false;
  if (!OPERAND_TAIL.test(scan.lastCodeChar)) return false;
  const last = scan.tokens[scan.tokens.length - 1];
  // The trailing word only disqualifies when it IS the last thing written —
  // `fn(x)` ends in `)` and is a completed operand even though its last word is
  // an identifier inside the call.
  if (last && last.start + last.word.length === scan.lastCodeEnd) {
    if (OPEN_EXPRESSION_KEYWORDS.has(last.word)) return false;
  }
  return true;
}

// ─── CASE expressions ────────────────────────────────────────────────────────

/** Words that may sit between `UPDATE` and the target relation. */
const UPDATE_TARGET_MODIFIERS: ReadonlySet<string> = new Set([
  'ONLY', 'IGNORE', 'LOW_PRIORITY',
]);

/** Which region of the innermost open CASE the cursor sits in. */
type CaseRegion = 'selector' | 'when' | 'then' | 'else';

/**
 * Names the cursor's region inside the innermost UNCLOSED `CASE` expression, or
 * null when the cursor is not inside one. Walks the barewords forward keeping a
 * CASE/END stack, so a CASE nested in another CASE's branch resolves to the
 * inner one and a closed `CASE … END` earlier in the statement is ignored.
 */
export function caseRegionAt(
  text: string,
  scanEnd: number,
  statementStart: number,
  opts: ScanOptions = {},
): CaseRegion | null {
  const stack: CaseRegion[] = [];
  for (const token of scanRegion(text, statementStart, scanEnd, opts).tokens) {
    switch (token.word) {
      case 'CASE':
        stack.push('selector');
        break;
      case 'END':
        stack.pop();
        break;
      case 'WHEN':
        if (stack.length > 0) stack[stack.length - 1] = 'when';
        break;
      case 'THEN':
        if (stack.length > 0) stack[stack.length - 1] = 'then';
        break;
      case 'ELSE':
        if (stack.length > 0) stack[stack.length - 1] = 'else';
        break;
      default:
        break;
    }
  }
  return stack.length > 0 ? stack[stack.length - 1]! : null;
}

/** True when an unclosed `CASE` encloses the cursor. Callers use it to refuse
 *  the clause-tail classifiers, which would otherwise read a half-written CASE
 *  branch as a finished projection or predicate. */
export function isInsideOpenCase(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return false;
  return caseRegionAt(text, scanEnd, statementStart, opts) !== null;
}

/** True when the cursor sits inside an `OVER ( … )` window definition. The
 *  ordering and frame keywords legal there differ from the statement-level
 *  ORDER BY tails (no LIMIT / OFFSET / FETCH; `ROWS` / `RANGE` instead). */
export function isInsideWindowDefinition(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return false;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return false;
  return wordBefore(text, openAt, statementStart) === 'OVER';
}

/**
 * The CASE-structure slot at the cursor, or null.
 *
 * `predicateTailSlot` is `nextKeywordSlot`'s completed-predicate classifier,
 * passed in so a WHEN predicate is judged by exactly the same rules as a WHERE
 * predicate (including its `IS ▮` / `BETWEEN ▮` refinements) instead of a
 * second, drifting copy.
 */
export function classifyCaseSlot(
  text: string,
  offset: number,
  statementStart: number,
  predicateTailSlot: (slot: NextKeywordSlot) => NextKeywordSlot | null,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const region = caseRegionAt(text, scanEnd, statementStart, opts);
  if (region === null) return null;

  switch (region) {
    // `CASE x ▮` — a simple CASE's selector is written; only WHEN can follow.
    // `CASE ▮` itself is ambiguous (a selector expression OR `WHEN`), so the
    // completed-operand test declines there and the caller keeps its columns.
    case 'selector':
      return isAfterCompletedOperand(text, scanEnd, statementStart, opts) ? 'after-case-selector' : null;
    case 'when':
      return predicateTailSlot('after-case-when-predicate');
    case 'then':
      return isAfterCompletedOperand(text, scanEnd, statementStart, opts) ? 'after-case-then-result' : null;
    case 'else':
      return isAfterCompletedOperand(text, scanEnd, statementStart, opts) ? 'after-case-else-result' : null;
  }
}

// ─── UPDATE / INSERT / CTE / window shapes ───────────────────────────────────

/**
 * `UPDATE <target> ▮` — the target relation is written and no `SET` has been
 * typed yet, so `SET` is the only clause that can follow. Declines at
 * `UPDATE ▮` itself, where a relation name is what belongs.
 */
export function classifyUpdateTargetSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const tokens = scan.tokens;
  const updateIdx = tokens.findIndex((t) => t.word === 'UPDATE');
  if (updateIdx === -1) return null;
  // `UPDATE` only introduces an update TARGET when it opens the statement, or
  // as a MERGE branch action (`WHEN MATCHED THEN UPDATE SET …`). Everywhere
  // else the same word belongs to something that takes no target — a row lock
  // (`SELECT … FOR UPDATE OF col`), PG's `FOR NO KEY UPDATE`, a privilege list
  // (`GRANT UPDATE ON …`).
  if (updateIdx !== 0 && tokens[updateIdx - 1]?.word !== 'THEN') return null;
  const after = tokens.slice(updateIdx + 1);
  if (after.length === 0) return null; // `UPDATE ▮` — a relation goes here.
  // A target-prefix modifier is not the target: `UPDATE ONLY ▮` (PG
  // inheritance), `UPDATE IGNORE ▮` / `UPDATE LOW_PRIORITY ▮` (MySQL) still
  // want the relation list.
  if (after.length === 1 && UPDATE_TARGET_MODIFIERS.has(after[0]!.word)) return null;
  if (after.some((t) => t.word === 'SET')) return null; // already past it.
  if (scan.parenDepth > 0) return null;
  return isAfterCompletedOperand(text, scanEnd, statementStart, opts) ? 'after-update-target' : null;
}

/**
 * `INSERT INTO t (cols) ▮` and `INSERT … VALUES (…) ▮` — both sit right after a
 * closed top-level paren; which one it is depends on whether a `VALUES` keyword
 * has been seen. Declines inside the parens, where the column / value lists are
 * what belongs.
 */
export function classifyInsertShapeSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  if (scan.parenDepth > 0) return null;
  if (scan.lastCodeChar !== ')') return null;
  const tokens = scan.tokens;
  if (tokens[0]?.word !== 'INSERT') return null;
  // A trailing `)` is not enough — it must be THIS statement's column list or a
  // VALUES tuple. Without the check, `INSERT INTO t SELECT a, COALESCE(b, 0) ▮`
  // (the call's paren) and `… ON CONFLICT (id) DO UPDATE SET a = f(x) ▮` both
  // answered with the row-source keywords, hiding the columns that belong there.
  const openAt = matchingOpenParen(text, scan.lastCodeEnd - 1, statementStart, opts);
  if (openAt === null) return null;
  const beforeParen = wordBefore(text, openAt, statementStart);
  const precedingTokens = scanRegion(text, statementStart, openAt, opts).tokens;
  const hasRowSource = precedingTokens.some((t) => t.word === 'VALUES' || t.word === 'SELECT');

  // `VALUES (…)` — the opener follows the keyword, or a comma continuing the
  // tuple list (`VALUES (1), (2) ▮`).
  const isTuple =
    beforeParen === 'VALUES' ||
    (precedingTokens.some((t) => t.word === 'VALUES') && charBefore(text, openAt, statementStart) === ',');
  if (isTuple) return 'after-insert-values';

  // `INSERT INTO t (cols) ▮` — the opener must directly follow the TARGET, so
  // everything between `INSERT` and it is the statement's own preamble. Without
  // that, MySQL's `INSERT INTO t SET c = f(x) ▮` matched on the call's paren.
  const preamble = precedingTokens.filter((t) => !INSERT_PREAMBLE_WORDS.has(t.word));
  if (!hasRowSource && beforeParen !== '' && beforeParen !== 'CONFLICT' && preamble.length <= 1) {
    return 'after-insert-columns';
  }
  return null;
}

/** Words that may appear between `INSERT` and its target's column list. What
 *  survives the filter is the target name itself — anything more means the
 *  paren belongs to something else. */
const INSERT_PREAMBLE_WORDS: ReadonlySet<string> = new Set([
  'INSERT', 'INTO', 'IGNORE', 'LOW_PRIORITY', 'HIGH_PRIORITY', 'DELAYED', 'OR', 'REPLACE',
]);

/** True when the word starting at `wordStart` is the tail of a dotted
 *  reference (`t.minus`) rather than a bare keyword. */
function isQualified(text: string, wordStart: number, lowBound: number): boolean {
  let i = wordStart;
  while (i > lowBound && /\s/.test(text[i - 1] ?? '')) i -= 1;
  return i > lowBound && text[i - 1] === '.';
}

/** The first non-whitespace char before `at`, or `''`. */
function charBefore(text: string, at: number, statementStart: number): string {
  let i = at;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  return i > statementStart ? (text[i - 1] ?? '') : '';
}

/**
 * `OVER (▮` — the head of a window specification, and `OVER (… ROWS ▮` — the
 * head of its frame. Neither offered its own keywords before: `PARTITION BY`
 * and `ORDER BY` were absent from the list entirely at the first, and the frame
 * extents at the second.
 */
export function classifyWindowSpecSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  // `OVER (▮` puts the caret immediately after the paren, which the shared
  // boundary helper refuses (it is neither whitespace nor a partial word), so
  // that one position is admitted explicitly.
  const scanEnd =
    slotScanEnd(text, offset, statementStart) ??
    (text[Math.min(offset, text.length) - 1] === '(' ? Math.min(offset, text.length) : null);
  if (scanEnd === null) return null;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return null;
  if (wordBefore(text, openAt, statementStart) !== 'OVER') return null;
  const inside = scanRegion(text, openAt + 1, scanEnd, opts);
  const words = inside.tokens.map((t) => t.word);
  // `OVER (▮` — nothing written yet.
  if (words.length === 0) return 'at-window-spec';
  // `OVER (… ROWS ▮` / `RANGE ▮` / `GROUPS ▮` — the frame unit is the last word.
  const last = inside.tokens[inside.tokens.length - 1]!;
  if (
    FRAME_UNITS.has(last.word) &&
    last.start + last.word.length === inside.lastCodeEnd &&
    // `OVER (ORDER BY t.rows ▮)` — a qualified column named `rows` is an order
    // item, not the frame unit.
    !isQualified(text, last.start, openAt + 1)
  ) {
    return 'after-window-frame-unit';
  }
  return null;
}

/** The window frame units — each takes an extent next. */
const FRAME_UNITS: ReadonlySet<string> = new Set(['ROWS', 'RANGE', 'GROUPS']);

/** Offset of the `(` matching the `)` at `closeAt`, or null when unbalanced.
 *  Forward-scans with the literal/comment-aware pass so a paren inside data
 *  cannot be mistaken for structure. */
function matchingOpenParen(
  text: string,
  closeAt: number,
  statementStart: number,
  opts: ScanOptions,
): number | null {
  // `scanRegion` up to the close reports the innermost paren still open there,
  // which IS the one this `)` closes.
  return scanRegion(text, statementStart, closeAt, opts).openParenAt;
}

/**
 * `WITH q AS (…) ▮` — the CTE body is closed, so the statement it feeds comes
 * next. (A `,` introducing another CTE is punctuation, not a completion.)
 */
export function classifyCteBodySlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  if (scan.parenDepth > 0 || scan.lastCodeChar !== ')') return null;
  if (scan.tokens[0]?.word !== 'WITH') return null;
  // The `)` must close a CTE BODY — `… AS ( … )`. Any other trailing paren in a
  // WITH statement (a function call in the main query's projection, a scalar
  // subquery) used to answer with the DML starters, replacing the columns.
  const openAt = matchingOpenParen(text, scan.lastCodeEnd - 1, statementStart, opts);
  if (openAt === null || wordBefore(text, openAt, statementStart) !== 'AS') return null;
  return 'after-cte-body';
}

/**
 * `OVER (PARTITION BY x ▮` — a completed partition item inside a window
 * definition, before any `ORDER BY` or frame clause. Ordering or a frame comes
 * next; a column would need a `,` first, which the user types rather than picks.
 */
export function classifyWindowPartitionSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const openAt = scan.openParenAt;
  if (openAt === null) return null;
  // The paren must be a window definition: `OVER (` or `OVER <name> (`.
  if (wordBefore(text, openAt, statementStart) !== 'OVER') return null;
  const inside = scanRegion(text, openAt + 1, scanEnd, opts).tokens.map((t) => t.word);
  if (inside[0] !== 'PARTITION' || inside[1] !== 'BY') return null;
  if (inside.includes('ORDER') || inside.includes('ROWS') || inside.includes('RANGE')) return null;
  if (inside.length < 3) return null; // `PARTITION BY ▮` — a column goes here.
  if (scan.lastCodeChar === ',') return null;
  return isAfterCompletedOperand(text, scanEnd, statementStart, opts)
    ? 'after-window-partition-item'
    : null;
}

/** Position kinds at which the INSERT / UPDATE / CTE shapes are worth testing —
 *  the clause machine names them for the statement the shape belongs to. */
export const SHAPE_POSITION_KINDS: ReadonlySet<CompletionPositionKind> = new Set<CompletionPositionKind>([
  'values',
  'set',
  'cte-def',
  'select-list',
  'from',
  'unknown',
  // A window definition sets the ORDER BY / GROUP BY clause from the inside
  // (`OVER (ORDER BY a ROWS ▮`), so those kinds must reach the window
  // classifiers before the clause tails claim them.
  'order-by',
  'group-by',
]);

// ─── multi-word construct heads ──────────────────────────────────────────────

/** The set operators that start a new query branch. Oracle spells EXCEPT
 *  `MINUS`. */
const SET_OPERATORS: ReadonlySet<string> = new Set(['UNION', 'INTERSECT', 'EXCEPT', 'MINUS']);

/**
 * Boundaries where the FIRST word of a multi-word construct has been typed and
 * only its completion is legal. Each of these used to be classified as
 * something else entirely — the set operators as a completed FROM relation
 * (yielding WHERE / JOIN / …, of which nothing is legal after `UNION`), the
 * others as a completed list item or an open predicate.
 */
/** Position kinds where an expression operator (`x NOT ▮`) can appear. Notably
 *  NOT `'unknown'`: a `CREATE TABLE (id INT NOT ▮)` column definition lands
 *  there, and `NULL` — the completion the user actually wants — is not an
 *  expression operator. */
const OPERATOR_POSITION_KINDS: ReadonlySet<CompletionPositionKind> = new Set<CompletionPositionKind>([
  'where', 'having', 'join-on', 'select-list', 'order-by', 'group-by',
]);

export function classifyKeywordHeadSlot(
  text: string,
  offset: number,
  statementStart: number,
  positionKind: CompletionPositionKind,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const scan = scanRegion(text, statementStart, scanEnd, opts);
  const word = scan.tokens[scan.tokens.length - 1];
  // The word must be the LAST thing written — punctuation after it (`UNION,`)
  // means it is not the caret's immediate predecessor.
  if (!word || word.start + word.word.length !== scan.lastCodeEnd) return null;
  // …and it must be a bare word. `SELECT t.minus ▮` / `ORDER BY t.nulls ▮` are
  // qualified COLUMNS whose name happens to match a keyword; treating them as
  // the keyword hid the column list.
  if (isQualified(text, word.start, statementStart)) return null;
  const up = word.word;
  const prev = scan.tokens[scan.tokens.length - 2]?.word ?? '';

  // `UNION ▮` / `EXCEPT ▮` / `MINUS ▮` → a new branch; `UNION ALL ▮` → SELECT.
  // UNION is split from the difference operators because `EXCEPT ALL` /
  // `INTERSECT ALL` are not universal (T-SQL and Oracle reject them). Gated out
  // of the projection: a set operator follows a COMPLETED query, so
  // `SELECT minus ▮` is a column of that name, not an operator.
  if (SET_OPERATORS.has(up) && positionKind !== 'select-list') {
    return up === 'UNION' ? 'after-set-operator' : 'after-difference-operator';
  }
  if ((up === 'ALL' || up === 'DISTINCT') && SET_OPERATORS.has(prev)) {
    return 'after-set-operator-quantifier';
  }
  // `ORDER BY x NULLS ▮` → FIRST / LAST. Gated to ORDER BY: `NULLS` is not a
  // reserved word, so `SELECT nulls ▮` is a column named `nulls`.
  if (up === 'NULLS' && positionKind === 'order-by') return 'after-nulls';
  // `WHERE x IS [NOT] DISTINCT ▮` → FROM.
  if (up === 'DISTINCT' && (prev === 'IS' || prev === 'NOT')) return 'after-is-distinct';
  // `GROUP BY x WITH ▮` → ROLLUP / CUBE.
  if (up === 'WITH' && isInGroupBy(text, word.start, statementStart, opts)) return 'after-group-by-with';
  // `x NOT ▮` → the negatable operators. A BARE `NOT ▮` (no left operand) is a
  // predicate head and must keep its columns, so an operand is required.
  if (
    up === 'NOT' &&
    prev !== 'IS' &&
    OPERATOR_POSITION_KINDS.has(positionKind) &&
    isAfterCompletedOperand(text, word.start, statementStart, opts)
  ) {
    return 'after-not-operator';
  }
  // The JOIN ladder. Each of these words commits the reader to a join, and the
  // continuation is a closed two- or three-word grammar — but the FROM tail
  // answered them with its own list, offering `WHERE` and a second `LEFT` after
  // `LEFT`. Gated to the FROM clause: `LEFT` / `RIGHT` are also string
  // functions, and `FULL` / `NATURAL` are ordinary names in some dialects.
  if (positionKind === 'from') {
    if (up === 'LEFT' || up === 'RIGHT' || up === 'FULL') return 'after-outer-join-side';
    if (up === 'OUTER' && (prev === 'LEFT' || prev === 'RIGHT' || prev === 'FULL')) {
      return 'after-outer';
    }
    if (up === 'NATURAL') return 'after-natural';
    if (up === 'CROSS') return 'after-cross';
    // `… FROM t USE ▮` — MySQL's index hints. The verbs are only hints AFTER a
    // relation; `USE db` is a statement of its own and `INSERT IGNORE` is a
    // modifier, so both need a FROM already in the statement.
    if (
      (up === 'USE' || up === 'FORCE' || up === 'IGNORE') &&
      scan.tokens.some((t) => t.word === 'FROM')
    ) {
      return 'after-index-hint-verb';
    }
  }
  // `PRIMARY ▮` / `FOREIGN ▮` → `KEY`. Both words are reserved in every dialect
  // and lead nothing else.
  if (up === 'PRIMARY' || up === 'FOREIGN') return 'after-key-constraint-head';
  // `INSERT OR ▮` — SQLite's conflict clause.
  if (up === 'OR' && prev === 'INSERT') return 'after-insert-or';
  // `… FOR JSON ▮` / `FOR XML ▮` — T-SQL's result serializers.
  if (prev === 'FOR' && up === 'JSON') return 'after-for-json';
  if (prev === 'FOR' && up === 'XML') return 'after-for-xml';
  return null;
}

/** True when the most recent clause keyword before `at` is `GROUP` — so a
 *  trailing `WITH` is MySQL/T-SQL's `WITH ROLLUP` modifier. */
function isInGroupBy(text: string, at: number, statementStart: number, opts: ScanOptions): boolean {
  const words = scanRegion(text, statementStart, at, opts).tokens.map((t) => t.word);
  for (let i = words.length - 1; i >= 0; i -= 1) {
    const w = words[i]!;
    if (w === 'GROUP') return true;
    if (w === 'SELECT' || w === 'FROM' || w === 'WHERE' || w === 'HAVING' || w === 'ORDER') {
      return false;
    }
  }
  return false;
}

/**
 * True when the cursor sits inside an open paren that is NOT a subquery — a
 * function call, a T-SQL hint list, a `PIVOT (…)`. The relation-tail
 * classifier must refuse there: `PIVOT (SUM(total) FOR ▮` is inside a `from`
 * clause and ends in a continuation keyword, so it used to be named a
 * completed relation and answered WHERE / JOIN / …, none of it legal.
 *
 * A derived table or `IN (SELECT …)` IS a subquery and keeps its normal
 * behavior — the relation tails are legal inside those.
 */
/** Scan end for a caret at the head of a paren group. The shared helper only
 *  accepts whitespace or a partial word that starts a token, so `f(▮` and
 *  `f(par▮` — both legal completion positions — come back null there. */
export function parenHeadScanEnd(
  text: string,
  offset: number,
  statementStart: number,
): number | null {
  const shared = slotScanEnd(text, offset, statementStart);
  if (shared !== null) return shared;
  let j = offset;
  while (j > statementStart && /[A-Za-z0-9_]/.test(text[j - 1] ?? '')) j -= 1;
  return text[j - 1] === '(' ? j : null;
}

/** `EXISTS (▮` — the only legal continuation is a query. Unlike `IN (`, which
 *  can open a value list, `EXISTS` admits nothing but a subquery, so the slot
 *  answers confidently and suppresses the column/table flood. Only the empty
 *  paren qualifies: once the subquery has started, its own clause machinery
 *  takes over. */
export function classifyExistsOpenSlot(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): NextKeywordSlot | null {
  const scanEnd = parenHeadScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return null;
  if (scanRegion(text, openAt + 1, scanEnd, opts).tokens.length > 0) return null;
  if (wordBefore(text, openAt, statementStart) !== 'EXISTS') return null;
  // `db.exists(▮` is a call to a user routine that happens to be named
  // `exists`, not the SQL predicate.
  let k = openAt;
  while (k > statementStart && /\s/.test(text[k - 1] ?? '')) k -= 1;
  while (k > statementStart && /[A-Za-z0-9_]/.test(text[k - 1] ?? '')) k -= 1;
  return text[k - 1] === '.' ? null : 'after-exists-open';
}

/** Words that can open a query expression inside a paren. `VALUES` and `TABLE`
 *  are complete queries in Postgres / SQLite, so a paren they open is a
 *  subquery paren like a `SELECT` one. */
const QUERY_OPENING_WORDS: ReadonlySet<string> = new Set(['SELECT', 'WITH', 'VALUES', 'TABLE']);

export function isInsideNonSubqueryParen(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return false;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return false;
  const inner = scanRegion(text, openAt + 1, scanEnd, opts).tokens;
  return inner.length > 0 && !QUERY_OPENING_WORDS.has(inner[0]!.word);
}
